import React, { useState } from 'react';
import PropTypes from 'prop-types';
// mui
import { Box, Card, CardHeader, CardContent, FormControlLabel, Grid, Stack, Switch, Select, MenuItem ,Typography} from '@mui/material';
//utils
import { showOnlyActiveLabels } from 'src/utils/showOnlyActiveLabels';
import { isNullOrEmpty } from 'src/utils/checkEmpty';
import { fCurrency } from 'src/utils/formatNumber';

// components
import EmptyContent from 'src/components/EmptyContent';
import LCChart from '../common/LCChart';
import Gallery from './components/Collection/Gallery';
import Table from './components/Collection/Table';
import ComingSoonCover from 'src/components/ComingSoonCover';
// ----------------------------------------------------------------------

Collection.propTypes = {
  mode: PropTypes.string,
  view: PropTypes.string,
  data: PropTypes.array
};

const labels = [
  'Jewelry',
  'Fine Art',
  'Wine & Spirits',
  'Musical Instruments',
  'Coins & Collectibles',
  'Furs',
  'Silver & Porcelain',
  'Other'
];

export default function Collection({ mode, view = 'show', data = [], chartData = {} }) {
  const [viewAll, setViewAll] = useState(false);

  const getValidData = () => {
    return viewAll ? data : data.filter((item) => item.assetStatus !== 'divested');
  };

  const calcStateExposure = () => {
    const statesSet = new Set();
    data.forEach((asset) => {
      statesSet.add(asset.loanAddress?.state || asset.address?.state);
    })
    return statesSet.size;
  }

  const chart1Data = [
    chartData.totalValueByClass?.jewelry || 0,
    chartData.totalValueByClass?.fineArt || 0,
    chartData.totalValueByClass?.wine_Spirits || 0,
    chartData.totalValueByClass?.musicalInstruments || 0,
    chartData.totalValueByClass?.coins_Collectibles || 0,
    chartData.totalValueByClass?.furs || 0,
    chartData.totalValueByClass?.silver_Porcelain || 0,
    chartData.totalValueByClass?.other || 0
  ];
  const chart2Data = [
    chartData.totalValueByPurchase?.jewelry_purchace || 0,
    chartData.totalValueByPurchase?.fineArt_purchace || 0,
    chartData.totalValueByPurchase?.wine_Spirits_purchace || 0,
    chartData.totalValueByPurchase?.musicalInstruments_purchace || 0,
    chartData.totalValueByPurchase?.coins_Collectibles_purchace || 0,
    chartData.totalValueByPurchase?.furs_purchace || 0,
    chartData.totalValueByPurchase?.silver_Porcelain_purchace || 0,
    chartData.totalValueByPurchase?.other_purchace || 0
  ];
  const chart3Data = [
    chartData.totalValueByEstate?.jewelry_estate || 0,
    chartData.totalValueByEstate?.fineArt_estate || 0,
    chartData.totalValueByEstate?.wine_Spirits_estate || 0,
    chartData.totalValueByEstate?.musicalInstruments_estate || 0,
    chartData.totalValueByEstate?.coins_Collectibles_estate || 0,
    chartData.totalValueByEstate?.furs_estate || 0,
    chartData.totalValueByEstate?.silver_Porcelain_estate || 0,
    chartData.totalValueByEstate?.other_estate || 0
  ];
  const chart4Data = [
    chartData.totalOfCollections?.jewelrySize || 0,
    chartData.totalOfCollections?.fineArtSize || 0,
    chartData.totalOfCollections?.wineSpiritsSize || 0,
    chartData.totalOfCollections?.musicalInstrumentsSize || 0,
    chartData.totalOfCollections?.coinsCollectiblesSize || 0,
    chartData.totalOfCollections?.fursSize || 0,
    chartData.totalOfCollections?.silverPorcelainSize || 0,
    chartData.totalOfCollections?.otherSize || 0
  ];

  const chartTitles = ['Purchase Basis', 'Market Value', 'Estate Basis'];
  const [selectedChartIndex, setSelectedChartIndex] = useState(0); // 0: Purchase, 1: Market, 2: Estate

  const customChartData = [showOnlyActiveLabels(labels, chart2Data), showOnlyActiveLabels(labels, chart1Data), showOnlyActiveLabels(labels, chart3Data), showOnlyActiveLabels(labels, chart4Data)];

  const renderData = () =>
    mode === 'gallery' ? (
      <Gallery data={data} />
    ) : (
      <Box sx={{ minHeight: 360 }}>
        <FormControlLabel
          sx={{ position: 'absolute', top: '46px', right: '46px', zIndex: 999 }}
          control={<Switch size="small" checked={viewAll} onChange={(event) => setViewAll(event.target.checked)} />}
          label={viewAll ? 'View Active' : 'View All'}
          labelPlacement="start"
        />
        <Table data={getValidData()} />
      </Box>
    );

  return (
    <Stack spacing={2}>
      {view === 'show' && (
        <Grid container>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6"> Number of Assets</Typography>
                  <Box sx={{ height: 'calc(100% - 12px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h3">{chartData.totalCollectionSize}</Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6">
                    Total Market Value
                  </Typography>
                  <Box sx={{ height: 'calc(100% - 12px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h3">{fCurrency(chartData.totalValueByClass.total)}</Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6">Total States with Exposure</Typography>
                  <Box sx={{ height: 'calc(100% - 12px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h3">{calcStateExposure()}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* TOTAL COLLECTION SIZE (ALWAYS VISIBLE) */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ m: 1, display: 'flex', flexDirection: 'column', pb: 3, height: 420 }}>
              {isNullOrEmpty(customChartData?.[3]?.customDataChart) && (
                <ComingSoonCover noData={true} style={{ marginTop: 25 }} />
              )}
              <CardHeader sx={{
                textAlign: 'center',
                '& .MuiCardHeader-content': {
                  width: '100%' // Make title span full width
                },
                '& .MuiCardHeader-action': {
                  position: 'absolute',
                  right: 12,
                  top: 12
                }
              }} title="Total Collection Size" />
              <Box sx={{ flex: 1 }}>
                <LCChart
                  type="quantity"
                  label={customChartData[3].customLabelsChart}
                  data={customChartData[3].customDataChart}
                />
              </Box>
            </Card>
          </Grid>
          
          {/* ONE SWITCHABLE COLLECTION VALUE CHART */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                m: 1,
                display: 'flex',
                flexDirection: 'column',
                pb: 3, // ✅ Adds bottom padding
                height: 420
              }}
            >
              <CardHeader
                sx={{
                  textAlign: 'center',
                  '& .MuiCardHeader-content': {
                    width: '100%' // Make title span full width
                  },
                  '& .MuiCardHeader-action': {
                    position: 'absolute',
                    right: 12,
                    top: 12
                  }
                }}
                title={
                  <Box sx={{ pt: 1 }}>Total Collection Value</Box>
                }
                action={
                  <Select
                    size="small"
                    value={selectedChartIndex}
                    onChange={(e) => setSelectedChartIndex(Number(e.target.value))}
                    sx={{
                      minWidth: 120,
                      fontSize: 12,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        px: 1.5
                      }
                    }}
                  >
                    {chartTitles.map((title, index) => (
                      <MenuItem key={title} value={index}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />

              <Box sx={{ flex: 1, pb: 2 }}> {/* ✅ Bottom padding inside chart box */}
                <LCChart
                  label={customChartData[selectedChartIndex].customLabelsChart}
                  data={customChartData[selectedChartIndex].customDataChart}
                />
              </Box>
            </Card>
          </Grid>

          

        </Grid>
      )}
      <Card>
        <CardContent>{data.length > 0 ? renderData() : <EmptyContent />}</CardContent>
      </Card>
    </Stack>
  );
}
